import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { motion } from 'framer-motion'
import { Mail, CheckCircle, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'

export function VerifyEmail() {
  const { user } = useAuth()
  const [isResending, setIsResending] = useState(false)
  const [resent, setResent] = useState(false)
  
  const handleResendEmail = async () => {
    if (!user?.email) return
    
    setIsResending(true)
    try {
      // In a real implementation, you would call a resend function
      // await supabase.auth.resend({ type: 'signup', email: user.email })
      setResent(true)
    } catch (error) {
      console.error('Error resending verification email:', error)
    } finally {
      setIsResending(false)
    }
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="bg-card rounded-2xl shadow-lg border p-8 text-center">
          {/* Icon */}
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
            <Mail className="w-8 h-8 text-primary" />
          </div>
          
          {/* Title and Description */}
          <h1 className="text-2xl font-bold text-foreground mb-4">
            Check Your Email
          </h1>
          <p className="text-muted-foreground mb-6">
            We've sent a verification link to{' '}
            <span className="font-medium text-foreground">
              {user?.email || 'your email address'}
            </span>
            . Click the link to activate your account.
          </p>
          
          {/* Success Message */}
          {resent && (
            <Alert className="mb-6">
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                Verification email sent successfully!
              </AlertDescription>
            </Alert>
          )}
          
          {/* Resend Button */}
          <div className="space-y-4">
            <Button 
              onClick={handleResendEmail}
              disabled={isResending || resent}
              variant="outline"
              className="w-full"
            >
              {isResending ? 'Sending...' : 'Resend verification email'}
            </Button>
            
            <Button asChild variant="ghost" className="w-full">
              <Link to="/auth/login">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to login
              </Link>
            </Button>
          </div>
          
          {/* Help Text */}
          <div className="mt-6 text-sm text-muted-foreground">
            <p>
              Didn't receive the email? Check your spam folder or{' '}
              <button 
                onClick={handleResendEmail}
                className="text-primary hover:text-primary/80 underline"
                disabled={isResending || resent}
              >
                try again
              </button>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  )
}